import {DurationConverter, RandomUtils, TimeUtils} from '@colonist/utils'
import {throttle} from 'lodash-es'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {UIUrlParameterNames} from '../ui_html/UIUrlController'
import {clientIntl} from '../ClientI18n'
import {UIPopupManager} from '../ui_popup/UIPopupManager'

export function turnSecondsInToTimerFormat(time: number): string {
    const hours = Math.floor(time / TimeUtils.MINUTES_IN_HOUR / TimeUtils.SECONDS_IN_MINUTE)
    const minutes = Math.floor(time % (TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR) / TimeUtils.SECONDS_IN_MINUTE)
    const seconds = Math.floor(time % TimeUtils.SECONDS_IN_MINUTE)

    let text = ''
    if(hours > 0) {
        text += hours + ':'
    }

    text += String(minutes).padStart(2, '0')
    text += ':'
    text += String(seconds).padStart(2, '0')
    return text
}

// https://stackoverflow.com/a/901144/4204919
export function getParameterByName(name: string, url?: string) {
    if(!url) url = window.location.href
    name = name.replace(/[[\]]/g, '\\$&')
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
    const results = regex.exec(url)
    if(!results) return null
    if(!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

// Apr 17, 2022 Omar: There is a bug in WebKit Safari 15.4 where it uses WebGL via Metal by default and breaks anti-aliasing.
// This tries to detect if the client is running a version of Safari that is affected by this bug, to disable anti-aliasing.
//
// Bug reference: https://bugs.webkit.org/show_bug.cgi?id=237906
//
export function isAffectedBySafariWebGlBug(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') == -1
    const isAffectedVersion = userAgent.indexOf('version/15.4') >= 0
    const isAffectedDevice = userAgent.match(/cpu (os|iphone os) 15_4 like mac os x/) !== null
    return isSafari && (isAffectedVersion || isAffectedDevice)
}

export function isMobileLandscapeView(): boolean {
    const breakpointWidthSmall = 480
    const breakpointWidthLarge = 991
    if(window.innerWidth < breakpointWidthSmall) return false
    if(window.innerWidth > breakpointWidthLarge) return false
    return true
}

export function sendToAppropriateMobileStore() {
    const random = RandomUtils.randomBool()
    const urlGooglePlay = 'https://play.google.com/store/apps/details?id=com.colonist.colonist'
    const urlAppStore = 'https://apps.apple.com/us/app/colonist-io/id6444831129'
    if(random) {
        navigateToPage(urlGooglePlay, true)
        return
    }
    navigateToPage(urlAppStore, true)
}

export function throttleUserAction(functionToThrottle: (...args: any[]) => void) {
    return throttle(functionToThrottle, DurationConverter.secondsAsMilliseconds(2), {trailing: false})
}

let inGloballyExclusiveHandler: boolean = false
export function createGloballyExclusiveHandler<T>(handler: (event: T) => void | Promise<void>) {
    return async(event: T) => {
        if(inGloballyExclusiveHandler) {
            return
        }
        inGloballyExclusiveHandler = true
        try {
            await handler(event)
        } finally {
            inGloballyExclusiveHandler = false
        }
    }
}

export function getHomepageUrl(): string {
    const languagePrefix = clientIntl.getLanguagePathPrefix()
    if(UIUtilsEjsVariables.isUserOnDiscordMobile) return `/.proxy${languagePrefix}/discord/mobile`
    if(UIUtilsEjsVariables.isUserOnDiscord) return `/.proxy${languagePrefix}/discord/web`
    if(UIUtilsEjsVariables.isUserOnMobile || UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return `${languagePrefix}/mobile`

    return `${languagePrefix}/`
}

export function getHomepageUrlWithParameter(parameter: UIUrlParameterNames, parameterValue: any): string {
    return `${getHomepageUrl()}#${parameter}=${parameterValue}`
}

export function navigateToPage(path: string, shouldOpenNewTab: boolean = false, shouldOpenInAppWithoutReload: boolean = false): void {
    UIPopupManager.closeAllPopups()

    const isExternalUrl = !path.startsWith('/') && !path.includes(window.location.origin)
    const isAuthUrl = path.includes('/auth/') || path.includes('/auth-link/')
    if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb && !isExternalUrl && !isAuthUrl) {
        if(shouldOpenInAppWithoutReload) return navigateToPageOnMobileAppViaLink(path)
        return navigateToPageOnMobileAppViaReload(path)
    }

    const url = path.startsWith('#') ? `${window.location.origin}/${path}` : path
    window.open(url, shouldOpenNewTab ? '_blank' : '_self')
}

function navigateToPageOnMobileAppViaReload(url: string) {
    const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url
    const parsedUrl = new URL(fullUrl)
    const relativeUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    const mobileUrl = relativeUrl.includes('/mobile') ? relativeUrl : `/mobile${relativeUrl}`

    history.pushState({}, '', mobileUrl)
    setTimeout(() => window.location.reload(), 100)
}

export function navigateToPageOnMobileAppViaLink(url: string) {
    const link = document.createElement('a')
    link.href = decodeURIComponent(url)

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}
